import { useState, useEffect, useCallback, useMemo } from "react";
import { X, ChevronRight, Search, Edit2, Calendar, Trash2, Plus, FileSpreadsheet, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { compactMoney, errorMessage, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

function NewClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ company_name: "", company: "", contact_person: "", email: "", phone: "", gstin: "", pan: "", billing_address: "", shipping_address: "", payment_terms: "Net 30", credit_limit: 0, account_manager: "", notes: "" });
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api("get", "/companies")
      .then((res) => {
        const active = (res.data || []).filter((c) => c.is_active);
        setCompanies(active);
        if (active.length > 0) {
          setForm((f) => ({ ...f, company: f.company || active[0].name }));
        }
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api("post", "/clients", { ...form, credit_limit: Number(form.credit_limit) });
      toast.success("Client added");
      onCreated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Please complete client details"));
    }
  };
  return (
    <div className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-client-form">
        <div className="modal-head"><div><div className="small-label">ACCOUNTS</div><h2>Add client</h2></div><button type="button" className="icon-btn" onClick={onClose} data-testid="new-client-close-button"><X size={18} /></button></div>
        <div className="form-grid">
          <label>
            Company / Organization <span style={{ color: "var(--red)" }}>*</span>
            <select
              required
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              data-testid="new-client-org-select"
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.company_id || c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>Company name<input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="new-client-company-input" /></label>
          <label>Contact person<input required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="new-client-contact-input" /></label>
          <label>Email<input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-client-email-input" /></label>
          <label>Phone<input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="new-client-phone-input" /></label>
          <label>GSTIN<input value={form.gstin} onChange={(e) => set("gstin", e.target.value)} data-testid="new-client-gstin-input" /></label>
          <label>PAN<input value={form.pan} onChange={(e) => set("pan", e.target.value)} data-testid="new-client-pan-input" /></label>
          <label>Payment terms<input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} data-testid="new-client-terms-input" /></label>
          <label>Credit limit<input type="number" value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} data-testid="new-client-credit-input" /></label>
          <label>Account manager<input value={form.account_manager} onChange={(e) => set("account_manager", e.target.value)} data-testid="new-client-manager-input" /></label>
        </div>
        <label className="full">Billing address<input value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="new-client-billing-input" /></label>
        <label className="full">Shipping address<input value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} data-testid="new-client-shipping-input" /></label>
        {error && <div className="error">{error}</div>}
        <button className="primary-btn wide" data-testid="new-client-submit-button">Add client <ChevronRight size={16} /></button>
      </form>
    </div>
  );
}

export function EditClientModal({ client, onClose, onUpdated }) {
  const [form, setForm] = useState({
    next_follow_up_date: client.next_follow_up_date || "",
    follow_up_notes: client.follow_up_notes || ""
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("patch", `/clients/${client.client_id}`, form);
      toast.success("Follow-up updated");
      onUpdated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not update follow-up"));
    } finally {
      setBusy(false);
    }
  };
  
  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ maxWidth: 480 }} data-testid="edit-client-followup-form">
        <div className="modal-head">
          <div>
            <div className="small-label">UPDATE FOLLOW-UP</div>
            <h2>{client.company_name}</h2>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <label className="full">
          Next follow-up date
          <input
            type="date"
            value={form.next_follow_up_date}
            min={getTodayDateString()}
            onChange={(e) => set("next_follow_up_date", e.target.value)}
            data-testid="edit-client-followup-date-input"
          />
        </label>
        <label className="full">
          Follow-up notes
          <textarea
            rows={3}
            value={form.follow_up_notes}
            onChange={(e) => set("follow_up_notes", e.target.value)}
            data-testid="edit-client-followup-notes-input"
            placeholder="Optional notes for the follow-up"
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button className="primary-btn wide" disabled={busy} data-testid="edit-client-followup-submit-button">
          {busy ? "Saving…" : "Update follow-up"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}

export function EditClientDetailsModal({ client, onClose, onUpdated }) {
  const [form, setForm] = useState({
    company_name: client.company_name || "",
    company: client.company || "",
    contact_person: client.contact_person || "",
    email: client.email || "",
    phone: client.phone || "",
    gstin: client.gstin || "",
    pan: client.pan || "",
    billing_address: client.billing_address || "",
    shipping_address: client.shipping_address || "",
    payment_terms: client.payment_terms || "Net 30",
    credit_limit: client.credit_limit || 0,
    account_manager: client.account_manager || "",
    notes: client.notes || ""
  });
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("get", "/companies")
      .then((res) => {
        const active = (res.data || []).filter((c) => c.is_active);
        setCompanies(active);
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("patch", `/clients/${client.client_id}`, {
        ...form,
        credit_limit: Number(form.credit_limit)
      });
      toast.success("Client updated");
      onUpdated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not update client"));
    } finally {
      setBusy(false);
    }
  };
  
  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-client-details-form">
        <div className="modal-head">
          <div>
            <div className="small-label">EDIT CLIENT</div>
            <h2>{client.company_name}</h2>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="form-grid">
          <label>
            Company / Organization
            <select
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              data-testid="edit-client-org-select"
            >
              <option value="">Select organization</option>
              {companies.map((c) => (
                <option key={c.company_id || c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>Company name<input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="edit-client-company-input" /></label>
          <label>Contact person<input required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="edit-client-contact-input" /></label>
          <label>Email<input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="edit-client-email-input" /></label>
          <label>Phone<input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="edit-client-phone-input" /></label>
          <label>GSTIN<input value={form.gstin} onChange={(e) => set("gstin", e.target.value)} data-testid="edit-client-gstin-input" /></label>
          <label>PAN<input value={form.pan} onChange={(e) => set("pan", e.target.value)} data-testid="edit-client-pan-input" /></label>
          <label>Payment terms<input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} data-testid="edit-client-terms-input" /></label>
          <label>Credit limit<input type="number" value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} data-testid="edit-client-credit-input" /></label>
          <label>Account manager<input value={form.account_manager} onChange={(e) => set("account_manager", e.target.value)} data-testid="edit-client-manager-input" /></label>
        </div>
        <label className="full">Billing address<input value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="edit-client-billing-input" /></label>
        <label className="full">Shipping address<input value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} data-testid="edit-client-shipping-input" /></label>
        {error && <div className="error">{error}</div>}
        <button className="primary-btn wide" disabled={busy} data-testid="edit-client-submit-button">
          {busy ? "Saving…" : "Update client"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}

export function AddOrderModal({ client, onClose, onCreated, onOpenOrder, onSwitchToBulk }) {
  const [form, setForm] = useState({
    description: "",
    product: "",
    product_code: "",
    quantity: 1,
    unit: "pcs",
    unit_price: "",
    required_delivery_date: "",
    po_number: "",
    po_date: "",
    priority: "Normal",
    notes: "",
    assigned_department: "Design",
    design_notes: ""
  });
  const [designFiles, setDesignFiles] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDesignFiles(files);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    
    try {
      const unitPriceVal = form.unit_price !== "" && form.unit_price !== null && !isNaN(Number(form.unit_price))
        ? Number(form.unit_price)
        : 0;

      // Create order with client_id and assigned_department
      const orderData = {
        client_id: client.client_id,
        description: form.description,
        product: form.product,
        product_code: form.product_code,
        quantity: Number(form.quantity),
        unit: form.unit || "pcs",
        unit_price: unitPriceVal,
        required_delivery_date: form.required_delivery_date,
        po_number: form.po_number,
        po_date: form.po_date,
        priority: form.priority,
        notes: form.notes,
        assigned_department: form.assigned_department
      };
      
      const response = await api("post", "/orders", orderData);
      const orderId = response.data.order_id;
      
      // If Design department selected and has design notes or files
      if (form.assigned_department === "Design") {
        // Upload design files if any
        if (designFiles.length > 0) {
          for (const file of designFiles) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("category", "Design Reference");
            
            try {
              await api("post", `/orders/${orderId}/documents`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
              });
            } catch (uploadErr) {
              console.error("File upload failed:", uploadErr);
              // Continue even if file upload fails
            }
          }
        }
        
        // Add design notes to order notes if provided
        if (form.design_notes && form.design_notes.trim()) {
          const updatedNotes = form.notes
            ? `${form.notes}\n\n--- Design Specifications ---\n${form.design_notes}`
            : `--- Design Specifications ---\n${form.design_notes}`;
          
          await api("patch", `/orders/${orderId}`, {
            notes: updatedNotes
          });
        }
      }
      
      const confStatus = response.data?.confirmation_email_status;
      if (confStatus === "Sent") {
        toast.success(`Order created & confirmation email sent to ${client.email || 'client'}`);
      } else {
        toast.success(
          form.assigned_department === "Production"
            ? "Order created and sent directly to Production"
            : "Order created successfully"
        );
      }
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not create order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="add-order-form">
        <div className="modal-head">
          <div>
            <div className="small-label">NEW ORDER FOR CLIENT</div>
            <h2>{client.company_name}</h2>
            <p className="muted">{client.contact_person} · {client.email}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {onSwitchToBulk && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              background: "#f8fafc",
              border: "1px solid var(--border)",
              borderRadius: 6,
              marginBottom: 16,
              fontSize: 12,
            }}
          >
            <span style={{ color: "var(--muted)" }}>Need to add multiple orders from Excel or CSV?</span>
            <button
              type="button"
              className="outline-btn compact"
              onClick={onSwitchToBulk}
              data-testid="switch-to-bulk-upload-btn"
              style={{ fontSize: 11, height: 26, padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <FileSpreadsheet size={12} /> Upload Excel / CSV
            </button>
          </div>
        )}
        
        <div className="form-grid">
          <label>
            Description
            <input
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="order-description-input"
              placeholder="e.g., Matte finish 350 GSM cards"
            />
          </label>
          <label>
            Product/Service *
            <input
              required
              value={form.product}
              onChange={(e) => set("product", e.target.value)}
              data-testid="order-product-input"
              placeholder="e.g., Visiting Cards"
            />
          </label>
          <label>
            Product Code
            <input
              value={form.product_code}
              onChange={(e) => set("product_code", e.target.value)}
              data-testid="order-product-code-input"
              placeholder="Optional"
            />
          </label>
          <label>
            Quantity *
            <input
              type="number"
              required
              min="1"
              value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              data-testid="order-quantity-input"
            />
          </label>
          <label>
            Unit
            <input
              value={form.unit || "pcs"}
              onChange={(e) => set("unit", e.target.value)}
              data-testid="order-unit-input"
              placeholder="pcs, boxes, sets"
            />
          </label>
          <label>
            Unit Price (₹)
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Optional"
              value={form.unit_price}
              onChange={(e) => set("unit_price", e.target.value)}
              data-testid="order-unit-price-input"
            />
          </label>
          <label>
            Delivery Deadline *
            <input
              type="date"
              required
              value={form.required_delivery_date}
              min={getTodayDateString()}
              onChange={(e) => set("required_delivery_date", e.target.value)}
              data-testid="order-deadline-input"
            />
          </label>
          <label>
            Priority
            <select
              value={form.priority}
              onChange={(e) => set("priority", e.target.value)}
              data-testid="order-priority-select"
            >
              <option value="Normal">Normal</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </label>
          <label>
            PO Number
            <input
              value={form.po_number}
              onChange={(e) => set("po_number", e.target.value)}
              data-testid="order-po-number-input"
              placeholder="Optional"
            />
          </label>
          <label>
            PO Date
            <input
              type="date"
              value={form.po_date}
              min={getTodayDateString()}
              onChange={(e) => set("po_date", e.target.value)}
              data-testid="order-po-date-input"
            />
          </label>
          <label>
            Assign to Department *
            <select
              value={form.assigned_department}
              onChange={(e) => set("assigned_department", e.target.value)}
              data-testid="order-department-select"
            >
              <option value="Order Received">Order Received (Default)</option>
              <option value="Design">Design</option>
              <option value="Production">Production</option>
            </select>
          </label>
        </div>

        <label className="full">
          Remarks/Notes
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            data-testid="order-notes-input"
            placeholder="Any special instructions or requirements..."
          />
        </label>

        {/* Design Specifications Section - Only show when Design is selected */}
        {form.assigned_department === "Design" && (
          <div style={{ 
            background: "#f0f9ff", 
            border: "1px solid #bae6fd",
            padding: 20, 
            borderRadius: 8, 
            marginTop: 16 
          }}>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 8, 
              marginBottom: 12,
              color: "#0369a1",
              fontWeight: 600
            }}>
              <span style={{ fontSize: 18 }}>🎨</span>
              <h3 style={{ margin: 0, fontSize: 15 }}>Design Specifications (Optional)</h3>
            </div>
            
            <p style={{ 
              margin: "0 0 16px 0", 
              fontSize: 13, 
              color: "#64748b",
              lineHeight: 1.5 
            }}>
              Upload reference files or add design instructions. These are optional - the designer can collect details later if needed.
            </p>

            <label className="full" style={{ marginBottom: 12 }}>
              Design Notes/Instructions
              <textarea
                rows={4}
                value={form.design_notes}
                onChange={(e) => set("design_notes", e.target.value)}
                data-testid="order-design-notes-input"
                placeholder="e.g., Color preferences, dimensions, special requirements, style guidelines..."
                style={{ 
                  background: "#fff",
                  border: "1px solid #cbd5e1"
                }}
              />
            </label>

            <label 
              className="full" 
              style={{ 
                cursor: "pointer",
                display: "block"
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 500
              }}>
                Reference Files (Images, Documents, etc.)
              </div>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleFileChange}
                data-testid="order-design-files-input"
                style={{ 
                  padding: "10px",
                  border: "2px dashed #cbd5e1",
                  borderRadius: 6,
                  background: "#fff",
                  width: "100%",
                  cursor: "pointer"
                }}
              />
              {designFiles.length > 0 && (
                <div style={{ 
                  marginTop: 8, 
                  fontSize: 12, 
                  color: "#0369a1",
                  fontWeight: 500
                }}>
                  ✓ {designFiles.length} file{designFiles.length !== 1 ? 's' : ''} selected
                  <div style={{ marginTop: 4, color: "#64748b", fontWeight: 400 }}>
                    {designFiles.map((f, i) => (
                      <div key={i}>• {f.name}</div>
                    ))}
                  </div>
                </div>
              )}
            </label>
          </div>
        )}

        {(() => {
          const priceVal = form.unit_price !== "" && form.unit_price !== null && !isNaN(Number(form.unit_price)) ? Number(form.unit_price) : 0;
          const qtyVal = Number(form.quantity) || 0;
          const subtotalVal = qtyVal * priceVal;
          const gstVal = subtotalVal > 0 ? subtotalVal * 0.18 : 0;
          const totalVal = subtotalVal + gstVal;
          return (
            <div style={{ 
              background: "#f8fafc", 
              padding: 16, 
              borderRadius: 8, 
              marginTop: 16,
              fontSize: 13,
              color: "var(--muted)"
            }}>
              <b style={{ color: "var(--navy)", display: "block", marginBottom: 8 }}>Order Summary</b>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div>Subtotal: <b>{subtotalVal > 0 ? `₹${subtotalVal.toFixed(2)}` : "₹0 (Not Added)"}</b></div>
                <div>GST (18%): <b>{gstVal > 0 ? `₹${gstVal.toFixed(2)}` : "₹0.00"}</b></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 15, color: "var(--navy)" }}>
                <b>Total: {totalVal > 0 ? `₹${totalVal.toFixed(2)}` : "₹0.00"}</b>
              </div>
            </div>
          );
        })()}

        {/* Automatic Email Notice */}
        <div style={{
          marginTop: 12,
          padding: "10px 14px",
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 12.5,
          color: "#166534"
        }} data-testid="add-order-email-notice">
          <span style={{ fontSize: 16 }}>✉️</span>
          <span>
            An automatic order confirmation email will be sent to <b style={{ color: "#14532d" }}>{client.email || "the client's registered email"}</b> once created.
          </span>
        </div>

        {error && <div className="error" style={{ marginTop: 16 }}>{error}</div>}
        
        <button
          className="primary-btn wide"
          disabled={busy}
          data-testid="order-submit-button"
          style={{ marginTop: 16 }}
        >
          {busy ? "Creating order…" : "Create order"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}

export default function Clients({ clients, onOpen, onChange, user, onSelectClient }) {
  const [q, setQ] = useSavedState("clients", "q", "");
  const [cardFilter, setCardFilter] = useSavedState("clients", "cardFilter", "");
  const [statusFilter, setStatusFilter] = useSavedState("clients", "statusFilter", "");
  const [termsFilter, setTermsFilter] = useSavedState("clients", "termsFilter", "");
  const [companyFilter, setCompanyFilter] = useSavedState("clients", "companyFilter", "");
  const [companiesList, setCompaniesList] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingDetails, setEditingDetails] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [busy, setBusy] = useState("");
  const canCreate = isAdmin(user) || canAccessDepartment(user, ["Sales"]);

  useEffect(() => {
    api("get", "/companies")
      .then((res) => {
        setCompaniesList(res.data || []);
      })
      .catch(() => {});
  }, []);

  // Client metrics for top summary cards
  const metrics = useMemo(() => {
    return {
      active: clients.filter((c) => (c.status || "Active") === "Active").length,
      credit: clients.filter((c) => Number(c.credit_limit || 0) > 0).length,
      followup: clients.filter((c) => Boolean(c.next_follow_up_date)).length,
      net30: clients.filter((c) => c.payment_terms === "Net 30").length,
    };
  }, [clients]);

  // Unique payment terms for dropdown filter
  const uniqueTerms = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => {
      if (c.payment_terms) set.add(c.payment_terms);
    });
    return Array.from(set);
  }, [clients]);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      // Summary card filter
      if (cardFilter === "active" && (c.status || "Active") !== "Active") return false;
      if (cardFilter === "credit" && Number(c.credit_limit || 0) <= 0) return false;
      if (cardFilter === "followup" && !c.next_follow_up_date) return false;
      if (cardFilter === "net30" && c.payment_terms !== "Net 30") return false;

      // Dropdown filters
      if (statusFilter && (c.status || "Active") !== statusFilter) return false;
      if (termsFilter && c.payment_terms !== termsFilter) return false;
      if (companyFilter && (c.company || "") !== companyFilter) return false;

      // Search query across Client/Company Name, Organization, Contact Person, Email, Phone, GSTIN, PAN, Client ID
      const query = q.trim().toLowerCase();
      if (!query) return true;

      const company = (c.company_name || "").toLowerCase();
      const org = (c.company || "").toLowerCase();
      const contact = (c.contact_person || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const phone = canViewClientPhone(user) ? (c.phone || "").toLowerCase() : "";
      const gstin = (c.gstin || "").toLowerCase();
      const pan = (c.pan || "").toLowerCase();
      const clientId = (c.client_id || "").toLowerCase();

      return (
        company.includes(query) ||
        org.includes(query) ||
        contact.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        gstin.includes(query) ||
        pan.includes(query) ||
        clientId.includes(query)
      );
    });
  }, [clients, q, cardFilter, statusFilter, termsFilter, companyFilter, user]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedClients,
    totalItems: totalFilteredClients,
  } = usePagination(filtered, 10, { sectionKey: "clients" });

  const handleDelete = async () => {
    const target = deletingClient;
    if (!target) return;
    setBusy("delete");
    try {
      await api("delete", `/clients/${target.client_id}`);
      toast.success(`${target.company_name} deleted successfully`);
      setDeletingClient(null);
      onChange();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Could not delete client";
      toast.error(errorMsg);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="content">
      <div className="page-heading">
        <div>
          <div className="small-label">DIRECTORY</div>
          <h1>Clients</h1>
          <p className="muted">Every client is connected to every downstream record.</p>
        </div>
        {canCreate && <button className="primary-btn compact" data-testid="clients-new-button" onClick={() => setShowNew(true)}>+ Add client</button>}
      </div>

      {/* Summary / Status Cards (Interactive Filters) */}
      <div className="metrics-grid">
        <MetricBox
          label="Active Clients"
          value={metrics.active}
          tone="blue"
          active={cardFilter === "active"}
          onClick={() => {
            const next = cardFilter === "active" ? "" : "active";
            setCardFilter(next);
            if (next) setStatusFilter("Active");
            else if (statusFilter === "Active") setStatusFilter("");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="With Credit Limit"
          value={metrics.credit}
          tone="purple"
          active={cardFilter === "credit"}
          onClick={() => {
            setCardFilter(cardFilter === "credit" ? "" : "credit");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Follow-up Scheduled"
          value={metrics.followup}
          tone="amber"
          active={cardFilter === "followup"}
          onClick={() => {
            setCardFilter(cardFilter === "followup" ? "" : "followup");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Net 30 Terms"
          value={metrics.net30}
          tone="green"
          active={cardFilter === "net30"}
          onClick={() => {
            const next = cardFilter === "net30" ? "" : "net30";
            setCardFilter(next);
            if (next) setTermsFilter("Net 30");
            else if (termsFilter === "Net 30") setTermsFilter("");
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Search & Filters Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            placeholder={canViewClientPhone(user) ? "Search client, contact, email, phone, GSTIN…" : "Search client, contact, email, GSTIN…"}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCurrentPage(1);
            }}
            data-testid="clients-search-input"
          />
          {q && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setQ("");
                setCurrentPage(1);
              }}
              data-testid="clients-search-clear"
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={companyFilter}
          onChange={(e) => {
            setCompanyFilter(e.target.value);
            setCurrentPage(1);
          }}
          data-testid="clients-company-filter"
        >
          <option value="">All organizations</option>
          {companiesList.map((comp) => (
            <option key={comp.company_id || comp.name} value={comp.name}>
              {comp.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            if (e.target.value === "Active") setCardFilter("active");
            else if (cardFilter === "active") setCardFilter("");
            setCurrentPage(1);
          }}
          data-testid="clients-status-filter"
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        {uniqueTerms.length > 0 && (
          <select
            value={termsFilter}
            onChange={(e) => {
              setTermsFilter(e.target.value);
              if (e.target.value === "Net 30") setCardFilter("net30");
              else if (cardFilter === "net30") setCardFilter("");
              setCurrentPage(1);
            }}
            data-testid="clients-terms-filter"
          >
            <option value="">All payment terms</option>
            {uniqueTerms.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
      </div>
      <section className="panel">
        <div className="panel-title"><div><h3>All clients <span className="count">{filtered.length}</span></h3></div></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Client</th><th>Organization</th><th>Contact</th><th>GSTIN</th><th>Payment terms</th><th>Credit limit</th><th>Manager</th><th>Status</th>{canCreate && <th style={{ textAlign: "right", width: 90 }}>Actions</th>}</tr></thead>
            <tbody>
              {paginatedClients.map((c) => (
                <tr key={c.client_id} data-testid={`client-row-${c.client_id}`}>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}><b>{c.company_name}</b><small className="mono">{c.client_id}</small></td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }} data-testid={`client-company-${c.client_id}`}>
                    {c.company ? (
                      <span
                        className="company-chip"
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          background: "#e0f2fe",
                          color: "#0369a1",
                          borderRadius: 4,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Building2 size={12} /> {c.company}
                      </span>
                    ) : (
                      <span style={{ color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}>{c.contact_person}<small>{c.email}</small></td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }} className="mono">{c.gstin || "—"}</td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}>{c.payment_terms}</td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}>{compactMoney(c.credit_limit)}</td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}>{c.account_manager || "—"}</td>
                  <td onClick={() => onSelectClient(c.client_id)} style={{ cursor: "pointer" }}><span className="badge green">{c.status}</span></td>
                  {canCreate && (
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button
                          className="icon-btn"
                          onClick={() => setEditingDetails(c)}
                          data-testid={`client-edit-${c.client_id}`}
                          title="Edit client"
                        >
                          <Edit2 size={14} />
                        </button>
                        {isAdmin(user) && (
                          <button
                            className="icon-btn"
                            onClick={() => setDeletingClient(c)}
                            data-testid={`client-delete-${c.client_id}`}
                            title="Delete client"
                            style={{ color: "var(--red)" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="empty-inline">No clients match this search.</div>}
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalFilteredClients}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="clients"
        />
      </section>
      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={onChange} />}
      {editingDetails && (
        <EditClientDetailsModal
          client={editingDetails}
          onClose={() => setEditingDetails(null)}
          onUpdated={() => {
            onChange();
            setEditingDetails(null);
          }}
        />
      )}
      {deletingClient && (
        <div className="modal-backdrop" style={{ zIndex: 10001 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="client-delete-confirm">
            <div className="modal-head">
              <div>
                <h2>Delete Client</h2>
              </div>
              <button type="button" className="icon-btn" onClick={() => setDeletingClient(null)}>
                <X size={18} />
              </button>
            </div>
            <div style={{ margin: "20px 0" }}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--navy)", lineHeight: 1.5 }}>
                Are you sure you want to delete this client?
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--muted)", lineHeight: 1.5 }}>
                <b style={{ color: "var(--navy)" }}>{deletingClient.company_name}</b> will be removed from your active clients list.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button
                className="outline-btn"
                onClick={() => setDeletingClient(null)}
                disabled={busy === "delete"}
                data-testid="client-delete-cancel"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={handleDelete}
                disabled={busy === "delete"}
                data-testid="client-delete-confirm-button"
                style={{ flex: 1, background: "var(--red)", borderColor: "var(--red)" }}
              >
                {busy === "delete" ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricBox({ label, value, tone = "blue", onClick, active }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={`client-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
      </div>
      <strong>{value}</strong>
    </div>
  );
}
